(function (browser) {
  'use strict';

  function describeSupply(label) {
    const text = String(label || '').trim().toLowerCase().replace(/\s+/g, ' ');
    let match = text.match(/^(\d+)\s*[-\u2010-\u2015 ]\s*months? supply$/);
    if (match && Number(match[1]) > 0 && Number.isSafeInteger(Number(match[1]))) return { kind: 'supply', units: Number(match[1]), months: Number(match[1]) };
    if (/^30\s*[-\u2010-\u2015 ]\s*day supply$/.test(text)) return { kind: 'supply', units: 1, months: 1 };
    match = text.match(/^(one|\d+)\s*[-\u2010-\u2015 ]?\s*packs?\s*\((\d+)\s*patch(?:es)?\)$/);
    if (match) {
      const packs = match[1] === 'one' ? 1 : Number(match[1]);
      const patches = Number(match[2]);
      if (packs > 0 && patches > 0 && Number.isSafeInteger(packs) && Number.isSafeInteger(patches)) return { kind: 'patches', units: patches, packs };
    }
    return null;
  }

  function findVariant(product, id) {
    return product?.variants?.find((variant) => String(variant.id) === String(id)) || null;
  }

  function findPlan(product, id) {
    return (product?.selling_plan_groups || []).flatMap((group) => group.selling_plans || [])
      .find((plan) => String(plan.id) === String(id)) || null;
  }

  function purchaseFacts(product, variantId, planId, quantity) {
    const variant = findVariant(product, variantId);
    if (!variant || !Number.isSafeInteger(quantity) || quantity < 1) return null;
    const allocation = planId && variant.selling_plan_allocations?.find((entry) => String(entry.selling_plan_id) === String(planId));
    if (planId && !allocation) return null;
    const price = allocation ? allocation.price : variant.price;
    if (!Number.isSafeInteger(price) || price < 0 || !Number.isSafeInteger(price * quantity)) return null;
    const supply = describeSupply(variant.title);
    const plan = allocation ? findPlan(product, planId) : null;
    const prices = new Set((allocation?.price_adjustments || []).map((entry) => entry.price).filter(Number.isSafeInteger));
    return { variant, supply, plan, quantity, price, total: price * quantity, subscription: Boolean(allocation), variablePrice: prices.size > 1 };
  }

  function planInterval(plan) {
    if (!plan) return '';
    const options = (plan.options || []).filter((option) => !/recharge plan id/i.test(option.name || ''));
    const choice = options.find((option) => /\b(day|week|month|year)s?\b/i.test(option.value || ''))?.value;
    const source = choice || plan.name || '';
    return String(source).split(':')[0].replace(/\s*[,\-]?\s*(?:save|extra)\s*\d+(?:\.\d+)?%.*$/i, '').trim();
  }

  function interpolate(template, values) {
    return String(template || '').replace(/\{\{\s*(\w+)\s*\}\}|__(\w+)__/g, (_, liquidKey, markerKey) => String(values[liquidKey || markerKey] ?? ''));
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = { describeSupply, purchaseFacts, planInterval, interpolate };
  if (!browser?.document) return;

  const script = document.currentScript;
  const sectionId = script?.dataset.pdpSectionId;
  if (!sectionId) return;
  const section = document.getElementById('shopify-section-' + sectionId);
  const configNode = document.getElementById('pdp-purchase-config-' + sectionId);
  const source = section?.querySelector('#product-data');
  const summary = section?.querySelector('[data-pdp-purchase-summary]');
  if (!section || !configNode || !source || !summary || section.dataset.pdpClarityInitialized) return;
  let config;
  let product;
  try {
    config = JSON.parse(configNode.textContent);
    const data = JSON.parse(source.textContent);
    product = data.product || data;
  } catch (_) { return; }
  const form = section.querySelector('form[action*="/cart/add"]');
  if (!form || String(config.currency) !== String(config.shopCurrency)) return;
  section.dataset.pdpClarityInitialized = 'true';
  section.classList.add('pdp-enhanced');
  const copy = config.copy || {};
  const formatter = new Intl.NumberFormat(config.locale || 'en', { style: 'currency', currency: config.currency, minimumFractionDigits: 2 });
  const money = (cents) => formatter.format(cents / 100);
  const text = (key, values = {}) => interpolate(copy[key], values);
  const setText = (node, value) => { if (node && node.textContent !== value) node.textContent = value; };
  const buttonRow = form.querySelector('.purchase-details__buttons');
  if (buttonRow) buttonRow.after(summary);
  let queued = false;
  let widgetRoot;
  let plansRoot;
  let widgetObserver;
  let plansObserver;
  let previousSelection;

  function attachWidget() {
    const widget = section.querySelector('recharge-subscription-widget');
    const current = widget?.shadowRoot || null;
    if (current !== widgetRoot) {
      widgetObserver?.disconnect();
      widgetRoot = current;
      if (current) {
        current.addEventListener('change', schedule);
        widgetObserver = new MutationObserver(schedule);
        widgetObserver.observe(current, { childList: true, subtree: true, attributes: true, attributeFilter: ['checked', 'value'] });
      }
    }
    const nextPlans = current?.querySelector('rc-selling-plans')?.shadowRoot || null;
    if (nextPlans !== plansRoot) {
      plansObserver?.disconnect();
      plansRoot = nextPlans;
      if (nextPlans) {
        nextPlans.addEventListener('change', schedule);
        plansObserver = new MutationObserver(schedule);
        plansObserver.observe(nextPlans, { childList: true, subtree: true, attributes: true, attributeFilter: ['selected', 'value'] });
      }
    }
    return widget;
  }

  function updateTiles(facts, planId) {
    section.querySelectorAll('.sa-option[data-variant-id]').forEach((tile) => {
      const variant = findVariant(product, tile.dataset.variantId);
      const supply = describeSupply(variant?.title);
      if (!variant || !supply) return;
      let unit = tile.querySelector('[data-pdp-unit-price]');
      if (unit) unit.hidden = true;
      let price = variant.price;
      if (facts.subscription) {
        const explicit = variant.selling_plan_allocations?.find((entry) => String(entry.selling_plan_id) === String(planId));
        const amounts = new Set((variant.selling_plan_allocations || []).map((entry) => entry.price));
        if (explicit) price = explicit.price;
        else if (amounts.size === 1) price = [...amounts][0];
        else return;
      }
      if (!Number.isSafeInteger(price) || price < 0) return;
      if (!unit) {
        unit = document.createElement('p');
        unit.className = 'pdp-unit-price';
        unit.dataset.pdpUnitPrice = '';
        tile.append(unit);
      }
      setText(unit, text(supply.kind === 'supply' ? 'perSupply' : 'perPatch', { price: money(price / supply.units) }));
      unit.hidden = false;
    });
  }

  function render() {
    queued = false;
    const widget = attachWidget();
    const purchaseOption = widgetRoot?.querySelector('input[name="purchaseOption"]:checked')?.value;
    const selectedPlan = form.querySelector('[name="selling_plan"]')?.value;
    const isSubscription = purchaseOption ? purchaseOption === 'subscription' : Boolean(selectedPlan);
    const planId = isSubscription ? selectedPlan || widget?.selectedVariantPlan?.external_plan_id : null;
    const variantId = form.querySelector('[name="id"]')?.value;
    const quantity = Number(form.querySelector('[name="quantity"]')?.value || 1);
    const facts = purchaseFacts(product, variantId, planId, quantity);
    if (!facts || isSubscription !== facts.subscription) {
      summary.hidden = true;
      section.querySelectorAll('[data-pdp-sticky-selection], [data-pdp-unit-price]').forEach((node) => { node.hidden = true; });
      return;
    }
    const contents = [facts.variant.title];
    if (facts.supply?.kind === 'patches' && quantity > 1) contents.push(text('patchCount', { count: facts.supply.units * quantity }));
    if (quantity > 1) contents.push(text('quantity', { quantity }));
    setText(summary.querySelector('[data-pdp-summary-contents]'), contents.join(' · '));
    const interval = planInterval(facts.plan);
    const priceCopy = text('shipmentPrice', { price: money(facts.total) });
    setText(summary.querySelector('[data-pdp-summary-price]'), priceCopy);
    const terms = facts.subscription
      ? [text('subscription'), interval && text('delivery', { interval }), text('renews'), facts.variablePrice && text('variablePrice')].filter(Boolean).join(' ')
      : text('oneTimeTerms');
    setText(summary.querySelector('[data-pdp-summary-terms]'), terms);
    summary.hidden = false;
    const stickySelection = section.querySelector('[data-pdp-sticky-selection]');
    const stickyPurchase = facts.subscription
      ? [text('subscription').replace(/\.$/, ''), interval].filter(Boolean).join(' · ')
      : text('oneTime');
    setText(stickySelection, [facts.variant.title, stickyPurchase].filter(Boolean).join(' · '));
    if (stickySelection) stickySelection.hidden = false;
    updateTiles(facts, planId);
    const key = [variantId, planId || 'one_time', quantity].join(':');
    if (previousSelection !== undefined && previousSelection !== key) {
      const metric = { component: 'pdp', purchase_type: facts.subscription ? 'subscription' : 'one_time', quantity, merchandise_value: facts.total / 100, currency: config.currency };
      if ([1, 3, 6, 12].includes(facts.supply?.months)) metric.supply_months = facts.supply.months;
      browser.PatchAidMeasurement?.track('selector_changed', metric);
    }
    previousSelection = key;
  }

  function schedule() {
    if (queued) return;
    queued = true;
    browser.requestAnimationFrame(render);
  }

  form.addEventListener('change', schedule);
  form.addEventListener('input', schedule);
  section.addEventListener('variant:changed', schedule);
  section.addEventListener('click', () => { schedule(); browser.setTimeout(schedule, 120); });
  new MutationObserver(schedule).observe(section, { childList: true, subtree: true });
  new MutationObserver(schedule).observe(form, { attributes: true, subtree: true, attributeFilter: ['value'] });
  browser.customElements?.whenDefined('recharge-subscription-widget').then(schedule);
  browser.customElements?.whenDefined('rc-selling-plans').then(schedule);
  browser.addEventListener('load', schedule, { once: true });
  const stickyBar = section.querySelector('.sticky-mobile-atc');
  if (stickyBar && browser.ResizeObserver) {
    new ResizeObserver(() => {
      document.documentElement.style.setProperty('--pdp-sticky-height', stickyBar.offsetHeight + 'px');
    }).observe(stickyBar);
  }
  schedule();
})(typeof window === 'undefined' ? null : window);
